/**
 * A corner badge naming the environment whose data a LOCALLY RUN stack is serving.
 *
 * Running locally, the browser URL is always `<tenant>.local.com`, whichever database the stack
 * behind it is pointed at — so the one thing the address bar cannot tell you is the thing that
 * matters most: whether you are looking at your own data or at production's. This says so.
 *
 * It appears only when the backend sends `X-Aysist-Local-Environment`, which it does only when its
 * conf carries `localEnvironment` — a key written exclusively by `scripts/dev-env.mjs` on a
 * developer's machine. Deployed configs come from ayton-config's ERB templates and have no such
 * key, so on any AWS environment the header is never sent and this never renders. There is no flag
 * that turns it on in a deployed application.
 *
 * Deliberately plain DOM rather than a React component: the React side of this application is a
 * registry of widgets that Dojo mounts through `window._renderComponent`, so there is no root that
 * owns the page chrome and a component would have to be rendered per widget. A single fixed element
 * appended to the body is both simpler and incapable of disturbing a Dojo mount.
 */

const ELEMENT_ID = 'sdb-local-environment-badge';

/** LOCAL is your own database and needs no warning; the rest are shared, and one is production. */
const STYLES: Record<string, { background: string; color: string }> = {
  LOCAL: { background: '#3c4858', color: '#ffffff' },
  DEVELOPMENT: { background: '#c77700', color: '#ffffff' },
  TEST: { background: '#c77700', color: '#ffffff' },
  ACCEPTANCE: { background: '#c77700', color: '#ffffff' },
  DEMO: { background: '#c77700', color: '#ffffff' },
  PRODUCTION: { background: '#b3261e', color: '#ffffff' }
};

const DEFAULT_STYLE = { background: '#3c4858', color: '#ffffff' };

/**
 * "REMOTE ACCEPTANCE" rather than "ACCEPTANCE", because the environment name alone is the half of
 * the sentence you already half-know. What is worth saying is that the data is not yours: the URL
 * says local.com, and the database is somewhere else entirely.
 *
 * LOCAL is the exception - it really is your own PostgreSQL, so calling it remote would be a lie.
 */
const label = (environment: string): string =>
  environment === 'LOCAL' ? 'LOCAL DATABASE' : `REMOTE ${environment}`;

let shown: string | null = null;

/**
 * Show, or update, the badge for `environment`.
 *
 * Safe to call on every API response: it does nothing once the environment it is already showing
 * comes back again, which is the case for all but the first call.
 */
export const showLocalEnvironmentBadge = (environment: string): void => {
  if (!environment) {
    return;
  }
  let badge = document.getElementById(ELEMENT_ID);
  // Memoised on the element as well as the value: Dojo replaces page content on navigation, which
  // can take the badge with it. Keying only on the environment name meant that once it had been
  // shown, every later response was a no-op and it never came back.
  if (badge && shown === environment) {
    return;
  }
  shown = environment;

  const style = STYLES[environment] || DEFAULT_STYLE;

  if (!badge) {
    badge = document.createElement('div');
    badge.id = ELEMENT_ID;
    // Top left, offset past the app-switcher button so it sits in the empty part of the header:
    // the bottom of the page is where you look last, and this needs to be seen before you touch
    // anything. pointer-events:none so it can never swallow a click meant for the header beneath.
    badge.style.cssText = [
      'position:fixed',
      'top:14px',
      'left:100px',
      'z-index:2147483647',
      'padding:4px 10px',
      'border-radius:4px',
      'font:600 11px/1.4 SourceSansPro-Bold, Arial, sans-serif',
      'letter-spacing:0.06em',
      'pointer-events:none',
      'user-select:none',
      'box-shadow:0 1px 4px rgba(0,0,0,0.3)'
    ].join(';');
    document.body.appendChild(badge);
  }

  badge.textContent = label(environment);
  badge.style.background = style.background;
  badge.style.color = style.color;
  badge.title =
    environment === 'LOCAL'
      ? 'This stack is running against your own local database'
      : `This stack runs locally but reads the ${environment} database`;
};

/** The header the backend sets; axios lower-cases header names. */
export const LOCAL_ENVIRONMENT_HEADER = 'x-aysist-local-environment';
